import React, { useState, useEffect, useRef } from 'react';
import './MoralAlignment.css';
import MoralAlignmentTooltip from './MoralAlignmentTooltip';
import { validateFrameMini } from '../../utils/apiHelpers';
import { Tag as TagType } from '../../types/types';
import { logEvent } from '../../utils/Logger';
interface MoralAlignmentProps {
  text: string;
  targetFrame: TagType[];
}

const isTagTypeListEqual = (a: TagType[], b: TagType[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].frame !== b[i].frame || a[i].likelihood !== b[i].likelihood || a[i].deselected !== b[i].deselected) {
      return false;
    }
  }
  return true;
}

const MoralAlignment: React.FC<MoralAlignmentProps> = ({ text, targetFrame }) => {
  const [isAligned, setIsAligned] = useState<boolean>(false);
  const [isTextEmpty, setIsTextEmpty] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastInputRef = useRef<number>(Date.now());
  const lastTextRef = useRef<string>(text);
  const lastFrameRef = useRef<TagType[]>(targetFrame);

  useEffect(() => {
    if (text !== lastTextRef.current || !isTagTypeListEqual(targetFrame, lastFrameRef.current)) {
      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Update last input time and text
      lastInputRef.current = Date.now();
      lastTextRef.current = text;
      lastFrameRef.current = targetFrame;

      if (text.length === 0) {
        setIsTextEmpty(true);
        return;
      }

      // Set a new timer
      timerRef.current = setTimeout(async () => {
        try {
          // console.log('fire api call at validateFrameMini');
          const finalTags = targetFrame.filter((tag) => tag.likelihood !== 'Extremely Unlikely' && tag.likelihood !== 'Unlikely' && !tag.deselected);
          setIsTextEmpty(false);
          setIsLoading(true);
          const result = await validateFrameMini(text, finalTags);
          setIsAligned(result.isAligned);
          setExplanation(result.explanation);
        } catch (error) {
          console.error('Error validating frame:', error);
        } finally {
          setIsLoading(false);
        }
      }, 750); // Following how Jakesch et al. (2023) determined the delay
    }

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [text, targetFrame]);

  const onMouseEnter = () => {
    logEvent('showMoralAlignmentTooltip', JSON.stringify({
      text: text,
      targetFrame: targetFrame,
      isAligned: isAligned,
    }), window.location.href);
    setShowTooltip(true);
  }

  const onMouseLeave = () => {
    logEvent('hideMoralAlignmentTooltip', JSON.stringify({
      text: text,
      targetFrame: targetFrame,
      isAligned: isAligned,
    }), window.location.href);
    setShowTooltip(false);
  }

  return (
    <div 
      className={`moral-alignment ${(isTextEmpty || isLoading) ? '' : isAligned === true ? 'aligned' : isAligned === false ? 'not-aligned' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      { isTextEmpty ? 'Waiting for comment...' :
        (isLoading ? 'Analyzing Moral Frame...' : 
          isAligned ? 
            <span className="aligned-text">Morally aligned</span> : 
            <span className="not-aligned-text">Morally unaligned</span>
        )
      }
      {showTooltip && <MoralAlignmentTooltip explanation={explanation} />}
    </div>
  );
};

export default MoralAlignment;